'use client';

import { type ComponentProps, type ReactNode, useId } from 'react';

import { tv } from '../../lib/tv';
import { Switch } from '../ui/switch';
import { useFieldControl } from './field';

export type ToggleProps = ComponentProps<typeof Switch> & {
  /** Figma "Label" - clickable. Omit only when a label is provided elsewhere (Field, aria-label). */
  label?: ReactNode;
};

const toggle = tv({
  slots: {
    root: 'inline-flex items-center gap-2',
    label:
      'text-body-md text-text-primary peer-data-disabled:text-text-disabled cursor-pointer peer-data-disabled:cursor-not-allowed',
  },
});

/**
 * Instant on/off setting that applies immediately (Figma: Components ↳
 * Controls ↳ Toggle). Use a Checkbox when the change needs a Save. A Radix
 * Switch: `role="switch"`, Space toggles; `checked` / `onCheckedChange`.
 */
export function Toggle({ label, className, ...props }: ToggleProps) {
  const generated = useId();
  const { id, ...rest } = useFieldControl(props);
  const styles = toggle();
  if (!label) return <Switch id={id} className={className} {...rest} />;
  const controlId = id ?? generated;
  return (
    <span data-slot="toggle-field" className={styles.root({ className })}>
      <Switch id={controlId} {...rest} />
      <label
        htmlFor={controlId}
        className={styles.label()}
        data-disabled={rest.disabled || undefined}
      >
        {label}
      </label>
    </span>
  );
}
